"use client";

import { useEffect, useRef, useState } from "react";
import { FixedLayout } from "@/components/shared/fixed-layout";
import { usePostKmcAuthRequest } from "@/lib/api/kmc/hooks";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { useSignupStore } from "@/lib/store/use-signup-store";

const PARENT_VERIFICATION_TITLE = "보호자 본인 인증";
const KMC_REQUEST_URL = "https://www.kmcert.com/kmcis/web/kmcisReq.jsp";
// KMC 콜백 페이지(POST /kmc/auth/callback-page)는 API 서버가 서빙한다.
// iframe 안 콜백 페이지 → 부모 창 postMessage 이므로 발신 오리진은 API 서버 오리진이다.
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8080";
const API_ORIGIN = new URL(API_BASE_URL, window.location.origin).origin;

export function ParentKmcVerificationStep() {
  const formRef = useRef<HTMLFormElement>(null);
  const [authData, setAuthData] = useState<{
    trCert: string;
    trUrl: string;
  } | null>(null);

  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();
  const { mutateAsync: requestAuth, isPending } = usePostKmcAuthRequest();
  const {
    parentInputName,
    parentInputBirth,
    setParentCertNum,
    goToNextStep,
    goToPrevStep,
  } = useSignupStore();

  useLoadingIndicator(isPending);

  useEffect(() => {
    const createAuthRequest = async () => {
      try {
        const response = await requestAuth({ plusInfo: "" });

        if (response.success) {
          setAuthData({
            trCert: response.data.trCert,
            trUrl: response.data.trUrl,
          });
        }
      } catch {
        setAlertDialogState({
          title: "본인인증 요청 실패",
          description: "본인인증 요청 생성에 실패했습니다. 다시 시도해주세요.",
          onConfirm: () => {
            resetAlertDialogState();
            goToPrevStep();
          },
        });
      }
    };

    createAuthRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // postMessage 리스너 등록
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // 보안: origin 검증 (API 서버가 서빙하는 콜백 페이지에서 온 메시지만 허용)
      if (event.origin !== API_ORIGIN) {
        return;
      }

      // KMC 콜백 데이터 수신
      if (event.data.type === "KMC_CALLBACK_RECEIVED") {
        const { apiToken, certNum } = event.data;

        if (!apiToken || !certNum) {
          setAlertDialogState({
            title: "본인인증 실패",
            description: "인증 정보가 올바르지 않습니다.",
            onConfirm: () => {
              resetAlertDialogState();
              goToPrevStep();
            },
          });
          return;
        }

        try {
          const response = await fetch(
            `${API_BASE_URL}/kmc/auth/callback`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ apiToken, certNum }),
            }
          );

          const result = await response.json();

          if (result.success) {
            const parentData = result.data;

            // 입력한 보호자 정보와 KMC 인증 결과 비교
            if (parentData.name !== parentInputName || parentData.birth !== parentInputBirth) {
              setAlertDialogState({
                title: "정보 불일치",
                description: "입력하신 보호자 정보가 본인인증 결과와 일치하지 않습니다.\n보호자 정보를 다시 입력해 주세요.",
                confirmText: "다시 입력하기",
                onConfirm: () => {
                  resetAlertDialogState();
                  goToPrevStep();
                },
              });
              return;
            }

            // certNum만 저장 (CI/DI는 서버 캐시에 저장됨)
            setParentCertNum(parentData.certNum);

            setAlertDialogState({
              title: "본인인증 완료",
              description: `${parentData.name}님의 본인인증이 완료되었습니다.`,
              onConfirm: () => {
                resetAlertDialogState();
                goToNextStep();
              },
            });
          } else {
            throw new Error("인증 처리 실패");
          }
        } catch {
          setAlertDialogState({
            title: "본인인증 처리 실패",
            description: "본인인증 처리 중 오류가 발생했습니다.",
            onConfirm: () => {
              resetAlertDialogState();
              goToPrevStep();
            },
          });
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [
    goToNextStep,
    goToPrevStep,
    parentInputBirth,
    parentInputName,
    resetAlertDialogState,
    setAlertDialogState,
    setParentCertNum,
  ]);

  // authData가 설정되면 자동으로 iframe에 form submit
  useEffect(() => {
    if (authData && formRef.current) {
      formRef.current.target = "kmcIframe";
      formRef.current.submit();
    }
  }, [authData]);

  return (
    <FixedLayout
      headerType="back"
      title={PARENT_VERIFICATION_TITLE}
      showButton={false}
      onBack={goToPrevStep}
      mainClassName="pb-0"
      contentClassName="mt-14.5 mb-0 px-0 overflow-hidden"
    >
      {/* KMC 본인인증 iframe */}
      {authData && (
        <div className="w-screen h-200 overflow-hidden">
          <iframe
            name="kmcIframe"
            title="KMC 본인인증"
            className="max-w-2xl w-screen h-full"
          />
          {/* KMC 본인인증 요청 폼 (숨김) */}
          <form
            ref={formRef}
            method="POST"
            action={KMC_REQUEST_URL}
            style={{ display: "none" }}
          >
            <input type="hidden" name="tr_cert" value={authData.trCert} />
            <input type="hidden" name="tr_url" value={authData.trUrl} />
            <input type="hidden" name="tr_add" value="Y" />
            <input type="hidden" name="tr_ver" value="V2" />
          </form>
        </div>
      )}
    </FixedLayout>
  );
}
